import {GlobalWorkerOptions, getDocument, type PDFDocumentProxy} from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {useCallback, useEffect, useRef, useState} from 'react';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

type PdfJsPreviewProps = {
  source: string;
  title: string;
  onReady(): void;
  onError(message: string): void;
};

const PdfPageCanvas = ({
  document,
  pageNumber,
  pageCount,
  onRendered,
  onError,
}: {
  document: PDFDocumentProxy;
  pageNumber: number;
  pageCount: number;
  onRendered(pageNumber: number): void;
  onError(message: string): void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    let renderTask: {cancel(): void; promise: Promise<void>} | undefined;

    void document.getPage(pageNumber)
      .then((page) => {
        if (cancelled || !canvasRef.current) return;
        const viewport = page.getViewport({scale: 1.35});
        const canvas = canvasRef.current;
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        canvas.style.aspectRatio = String(viewport.width) + ' / ' + String(viewport.height);
        renderTask = page.render({canvas, viewport}) as typeof renderTask;
        return renderTask?.promise;
      })
      .then(() => {
        if (!cancelled) onRendered(pageNumber);
      })
      .catch((error: unknown) => {
        if (cancelled || (error instanceof Error && error.name === 'RenderingCancelledException')) return;
        onError('The protected PDF could not be rendered.');
      });

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [document, onError, onRendered, pageNumber]);

  return (
    <canvas
      ref={canvasRef}
      className="pd-pdf-page"
      role="img"
      aria-label={'PDF page ' + pageNumber + ' of ' + pageCount}
    />
  );
};

export const PdfJsPreview = ({source, title, onReady, onError}: PdfJsPreviewProps) => {
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [renderedPages, setRenderedPages] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    const loadingTask = getDocument({url: source});
    setDocument(null);
    setRenderedPages(new Set());

    void loadingTask.promise
      .then((loaded) => {
        if (cancelled) return;
        if (loaded.numPages < 1 || loaded.numPages > 50) {
          void loadingTask.destroy();
          onError('This PDF has an unsupported number of pages.');
          return;
        }
        setDocument(loaded);
      })
      .catch(() => {
        if (!cancelled) onError('The protected PDF could not be opened.');
      });

    return () => {
      cancelled = true;
      void loadingTask.destroy();
    };
  }, [onError, source]);

  const markRendered = useCallback((pageNumber: number) => {
    setRenderedPages((current) => {
      if (current.has(pageNumber)) return current;
      const next = new Set(current);
      next.add(pageNumber);
      return next;
    });
  }, []);

  useEffect(() => {
    if (document && renderedPages.size === document.numPages) onReady();
  }, [document, onReady, renderedPages]);

  const reportError = useCallback((message: string) => onError(message), [onError]);

  return (
    <section
      className="pd-pdf-viewer"
      aria-label={title}
      title={title}
      data-pdf-source={source}
      tabIndex={0}
    >
      {document
        ? Array.from({length: document.numPages}, (_, index) => (
          <PdfPageCanvas
            key={index + 1}
            document={document}
            pageNumber={index + 1}
            pageCount={document.numPages}
            onRendered={markRendered}
            onError={reportError}
          />
        ))
        : null}
    </section>
  );
};
